/* eslint-disable max-lines-per-function */
// M4-T01 — Owner FILTER (dashboard top bar). A multi-select of org users plus an
// "Unassigned" option, rendered as removable chips. It writes through the existing
// sidebar-filter engine (addFilter/removeFilter "owner_user_id", type "array"), so
// the selection reaches applicantSearch via the same ElasticFilterInput pipeline.
import React from "react";
import { useQuery } from "@apollo/client";
import { Autocomplete, AutocompleteRenderInputParams, Box, TextField } from "@mui/material";
import { ASSIGNABLE_OWNERS } from "components/common/owners-editor/owner-queries";
import OwnerChip from "components/common/owners-editor/owner-chip";
import {
  AssignableOwnersData,
  OwnerOption,
  buildOptions,
  ownerOptionKey,
  ownersFromData,
} from "components/common/owners-editor/owner-select";
import UnassignedOwner from "../interests-table/unassigned-owner";
import { useSidebarFilters } from "../sidebar-filters";

const OWNER_FILTER_FIELD = "owner_user_id";

// Sentinel carried in `values` for the "Unassigned" selection. MUST match the
// server's @unassigned_owner_sentinel in query_assembler.ex.
export const UNASSIGNED_OWNER_SENTINEL = "__unassigned__";

// The "Unassigned" pseudo-option. Its userId is the sentinel and its empty group
// pins it to the very top.
const UNASSIGNED_OPTION: OwnerOption = {
  userId: UNASSIGNED_OWNER_SENTINEL,
  label: "Unassigned",
  group: "",
  isMe: false,
};

type OwnerFilterProps = {

  /** Current user id, for the pinned "Me (Name)" option (mirrors the inline cell). */
  currentUserId: string;

  /** Current user display name, for the "Me (Name)" label. */
  currentUserName: string;
};

const OwnerFilter: React.FC<OwnerFilterProps> = ({ currentUserId, currentUserName }) => {
  const { addFilter, removeFilter, filterState } = useSidebarFilters();

  const { data, loading } = useQuery<AssignableOwnersData>(ASSIGNABLE_OWNERS, {
    fetchPolicy: "cache-first",
  });

  const options = React.useMemo<OwnerOption[]>(
    () => [
      UNASSIGNED_OPTION,
      ...buildOptions(ownersFromData(data), currentUserId, currentUserName),
    ],
    [data, currentUserId, currentUserName],
  );

  // Filter state is the source of truth, so selection is by user (not property
  // team) and stays in sync when a chip is removed elsewhere.
  const selectedIds: string[] = React.useMemo(() => {
    const values = filterState[OWNER_FILTER_FIELD]?.value;
    return Array.isArray(values) ? (values as string[]) : [];
  }, [filterState]);

  const value = React.useMemo<OwnerOption[]>(
    () =>
      selectedIds.map(
        (id) =>
          options.find((option) => option.userId === id) ?? {
            userId: id,
            label: id === UNASSIGNED_OWNER_SENTINEL ? "Unassigned" : id,
            group: "",
            isMe: false,
          },
      ),
    [selectedIds, options],
  );

  const applySelection = (next: OwnerOption[]) => {
    // De-dupe by userId — a user listed under multiple property groups is one
    // selection (one chip, one id).
    const ids = Array.from(new Set(next.map((option) => option.userId)));
    if (ids.length === 0) {
      removeFilter(OWNER_FILTER_FIELD, "array");
    } else {
      addFilter(OWNER_FILTER_FIELD, ids, "array");
    }
  };

  const removeOwner = (userId: string) =>
    applySelection(value.filter((option) => option.userId !== userId));

  return (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
      <Autocomplete<OwnerOption, true>
        multiple
        id="owner-filter-select"
        data-cy="owner-filter"
        sx={{ minWidth: 200, flexGrow: 1 }}
        options={options}
        loading={loading}
        value={value}
        disableCloseOnSelect
        getOptionLabel={(option) => option.label}
        getOptionKey={ownerOptionKey}
        groupBy={(option) => option.group}
        isOptionEqualToValue={(option, optionValue) => option.userId === optionValue.userId}
        onChange={(_event, next) => applySelection(next)}
        // S4: the picker is ADD-only; the selection shows as standalone chips below.
        renderTags={() => null}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField
            {...params}
            size="small"
            label="Owner"
            placeholder="Filter by owner"
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                "aria-label": "Filter by owner",
                "data-cy": "owner-filter-input",
              },
            }}
          />
        )}
      />
      {value.length > 0 && (
        <Box
          component="ul"
          role="list"
          aria-label="Selected owner filters"
          data-cy="owner-filter-chips"
          data-testid="owner-filter-chips"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.5,
            listStyle: "none",
            p: 0,
            m: 0,
          }}
        >
          {value.map((option) => {
            const isUnassigned = option.userId === UNASSIGNED_OWNER_SENTINEL;
            const removeLabel = isUnassigned ? "Remove no-owner filter" : `Remove owner ${option.label}`;
            return (
              <Box component="li" key={option.userId} sx={{ display: "inline-flex" }}>
                <OwnerChip
                  data-cy="owner-filter-chip"
                  data-testid="owner-filter-chip"
                  label={isUnassigned ? <UnassignedOwner /> : option.label}
                  ariaLabel={isUnassigned ? "No owner" : `Owner: ${option.label}`}
                  onDelete={() => removeOwner(option.userId)}
                  deleteAriaLabel={removeLabel}
                />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default OwnerFilter;
